/* @flow */

import * as React from 'react'
import { Field } from 'formik'
import get from 'lodash/get'
import styled, { css } from 'styled-components'

import {
  CheckboxInput,
  ColorInput,
  ControlLabel,
  FormGroup,
  FormInput,
  FilesInput,
  FilesExistingValue,
  RadioInput,
  SelectInput,
  unwrapTemporaryFile,
} from '../../../../infrastructure/components/Formik'
import FontSelector from '../../../../infrastructure/components/FontSelector'
import { SpacingSettings, SettingsSeparator } from '../WebshopWidgets/shared'

import webshopHeaderLogoPositionLeft from './assets/webshop-header-logo-position-left.png'
import webshopHeaderLogoPositionCenter from './assets/webshop-header-logo-position-center.png'

const WebshopHeaderSettings = ({ values }) => {
  const [replaceExistingBackdropLogo, setReplaceExistingBackdropLogo] =
    React.useState(false)

  const unwrappedBackdropLogoOverride = React.useMemo(() => {
    let unwrappedBackdropLogoOverride = null

    if (values.data.backdrop_logo_override) {
      unwrappedBackdropLogoOverride = unwrapTemporaryFile(
        values.data.backdrop_logo_override
      )
    }

    return unwrappedBackdropLogoOverride
  }, [values.data.backdrop_logo_override])

  return (
    <div>
      <FormGroup>
        <ControlLabel>Design type</ControlLabel>

        <RadioInput
          id="webshop-header-settings-type-modern"
          name="data.type"
          label="Modern"
          value="modern"
        />

        <RadioInput
          id="webshop-header-settings-type-classic"
          name="data.type"
          label="Classic"
          value="classic"
        />
      </FormGroup>

      {values.data.type === 'modern' && (
        <>
          <SettingsSeparator />

          <FormGroup>
            <ControlLabel>Logo position</ControlLabel>
            <Field name="data.logo_position" component={LogoPositionField} />
          </FormGroup>

          <FormGroup>
            <ControlLabel>Logo size</ControlLabel>
            <LogoSizeInputsContainer>
              <SelectInput
                name={`data.logo_size_mode`}
                options={logoSizeModeOptions}
                simpleValue
                clearable={false}
              />
              {values.data.logo_size_mode === 'set_width' && (
                <>
                  <FormInput name="data.logo_width" type="number" min={0} />
                  <div>px</div>
                </>
              )}
              {values.data.logo_size_mode === 'set_height' && (
                <>
                  <FormInput name="data.logo_height" type="number" min={0} />
                  <div>px</div>
                </>
              )}
            </LogoSizeInputsContainer>
          </FormGroup>

          <FormGroup>
            <ControlLabel>Logo when on backdrop</ControlLabel>
            {(replaceExistingBackdropLogo ||
              !unwrappedBackdropLogoOverride) && (
              <FilesInput
                allowMultiple={false}
                name="data.backdrop_logo_override"
              />
            )}
            <FilesExistingValue
              // We want logo as png so it is transparent
              imageFormat="png"
              maxWidth="250px"
              onChange={() => setReplaceExistingBackdropLogo(true)}
              shouldChange={replaceExistingBackdropLogo}
              value={unwrappedBackdropLogoOverride}
            />
          </FormGroup>

          <SettingsSeparator />

          <FormGroupsRow>
            <FormGroup>
              <ControlLabel>Navigation font</ControlLabel>
              <SelectInput
                name="data.navigation_font"
                selectComponent={FontSelector}
                simpleValue
              />
            </FormGroup>
            <FormGroup>
              <ControlLabel>Font size</ControlLabel>
              <NumberInputWithUnitLabelContainer>
                <FormInput
                  name="data.navigation_font_size"
                  type="number"
                  min={1}
                />
                <div>px</div>
              </NumberInputWithUnitLabelContainer>
            </FormGroup>
          </FormGroupsRow>

          <FormGroupsRow gridTemplateColumns="90px auto">
            <FormGroup>
              <ControlLabel>Font color</ControlLabel>
              <ColorInput name="data.navigation_font_color" />
            </FormGroup>
            <FormGroup>
              <ControlLabel>On hover</ControlLabel>
              <ColorInput name="data.navigation_font_hover_color" />
            </FormGroup>
          </FormGroupsRow>

          <FormGroupsRow gridTemplateColumns="90px auto">
            <FormGroup>
              <ControlLabel>On backdrop</ControlLabel>
              <ColorInput name="data.backdrop_navigation_font_color" />
            </FormGroup>
            <FormGroup>
              <ControlLabel>On hover on backdrop</ControlLabel>
              <ColorInput name="data.backdrop_navigation_font_hover_color" />
            </FormGroup>
          </FormGroupsRow>

          <FormGroup>
            <CheckboxInput
              id="data.navigation_uppercase"
              name="data.navigation_uppercase"
              label="Navigation uppercase letters"
            />
            <CheckboxInput
              id="data.navigation_hover_underline"
              name="data.navigation_hover_underline"
              label="Navigation underline on hover"
            />
          </FormGroup>

          <SettingsSeparator />

          <FormGroupsRow gridTemplateColumns="115px auto">
            <FormGroup>
              <ControlLabel>Space between navigation items</ControlLabel>
              <NumberInputWithUnitLabelContainer>
                <FormInput
                  name="data.navigation_space_between_each"
                  type="number"
                  min={0}
                />
                <div>px</div>
              </NumberInputWithUnitLabelContainer>
            </FormGroup>
            <FormGroup>
              <ControlLabel>Space between logo and navigation</ControlLabel>
              <NumberInputWithUnitLabelContainer>
                <FormInput
                  name="data.space_between_logo_and_navigation"
                  type="number"
                  min={0}
                />
                <div>px</div>
              </NumberInputWithUnitLabelContainer>
            </FormGroup>
          </FormGroupsRow>

          <SpacingSettings />
        </>
      )}
    </div>
  )
}

export default WebshopHeaderSettings

const LogoPositionField = ({
  field: { onBlur, name, onChange, value },
  form: { setFieldValue, touched, errors },
}) => {
  return (
    <LogoPositionContainer>
      <LogoPositionOptionButton
        type="button"
        active={value === 'left'}
        onClick={() => setFieldValue(name, 'left')}
      >
        <LogoPositionLeftIllustration active={value === 'left'} />
        <LogoPositionLabel>Left</LogoPositionLabel>
      </LogoPositionOptionButton>
      <LogoPositionOptionButton
        type="button"
        active={value === 'center'}
        onClick={() => setFieldValue(name, 'center')}
      >
        <LogoPositionCenterIllustration active={value === 'center'} />
        <LogoPositionLabel>Center</LogoPositionLabel>
      </LogoPositionOptionButton>
    </LogoPositionContainer>
  )
}

const LogoPositionContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
`

const LogoPositionOptionButton = styled.button`
  padding: 5px 0;
  margin: 0;

  border-radius: 3px;
  border: 1px dashed #ccc;
  background: #fff;

  outline: none;
  color: #777777;

  &:hover,
  &:focus-visible {
    border-color: #6c6c6c;
  }

  ${props =>
    props.active &&
    css`
      border-color: #000000;
      color: #000000;
    `}
`

const LogoPositionLabel = styled.div`
  font-size: 11px;
  line-height: 1;
  font-weight: bold;
  text-align: center;
`

const LogoPositionLeftIllustration = ({ active }: { active: boolean }) => {
  const fillColor = active ? '#000000' : '#777777'

  return (
    <svg
      width="116"
      height="14"
      viewBox="0 0 116 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect x="18" y="5" width="56" height="4" fill={fillColor} />
      <circle cx="7" cy="7" r="7" fill={fillColor} />
      <circle cx="113" cy="7" r="3" fill={fillColor} />
      <circle cx="105" cy="7" r="3" fill={fillColor} />
      <circle cx="97" cy="7" r="3" fill={fillColor} />
    </svg>
  )
}

const LogoPositionCenterIllustration = ({ active }: { active: boolean }) => {
  const fillColor = active ? '#000000' : '#777777'

  return (
    <svg
      width="118"
      height="14"
      viewBox="0 0 118 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect y="5" width="40" height="4" fill={fillColor} />
      <circle cx="61" cy="7" r="7" fill={fillColor} />
      <circle cx="115" cy="7" r="3" fill={fillColor} />
      <circle cx="107" cy="7" r="3" fill={fillColor} />
      <circle cx="99" cy="7" r="3" fill={fillColor} />
    </svg>
  )
}

const logoSizeModeOptions = [
  {
    label: 'Set logo height',
    value: 'set_height',
  },
  {
    label: 'Set logo width',
    value: 'set_width',
  },
]

const LogoSizeInputsContainer = styled.div`
  display: grid;
  grid-template-columns: 130px 70px auto;
  gap: 8px;
  align-items: center;
`

const NumberInputWithUnitLabelContainer = styled.div`
  display: grid;
  grid-template-columns: 70px auto;
  gap: 8px;
  align-items: center;
`

const FormGroupsRow = styled.div`
  display: grid;
  grid-template-columns: ${props => props.gridTemplateColumns || 'auto auto'};
  gap: 20px;
`
